export enum CalendarFilterVariants {
  IS_PRIMARY_CALENDAR_FILTER = 'is_primary_calendar_filter',
  IS_SECONDARY_CALENDAR_FILTER = 'is_secondary_calendar_filter',
}

const TOURS_FLAGS = {
  TOURS_CHEAPER_HOTEL_OFFERS: 'trfree_cheaper_hotel_offers',
  TOURS_OFFER_IMAGE_FORMAT: 'trfree_offers_image_format',
  TOURS_COMPLEX_ROUTES: 'trfree_complex_routes',
  TOURS_ONBOARDING_FULLSCREEN: 'trfree_onboarding_fullscreen',
  TOURS_BEST_SELLERS: 'trfree_best_sellers',
  TOURS_MAIN_PAGE_HOT_TOURS: 'trfree_main_page_hot_tours',
  TOURS_TRAVEL_SALE_PROMO: 'trfree_travel_sale_promo',
  TOURS_SIMILAR_HOTELS: 'trfree_similar_hotels',
  /** A/B: while running, offer cards show a hardcoded installment term (control = 6 months, treatment = 9) and the payment page limits API loan options to 6 months for control. Treatment variant key: `increased_factoring_on`. */
  TOURS_INCREASED_FACTORING: 'trfree_increased_factoring',
  TOURS_HOTEL_DETAILS_OFFERS: 'trfree_hotel_details_offers',
  /** A/B: skips the buyer confirmation step (BookingConfirmPage) for variant B. Control variant key: `confirm_page_on`. */
  TOURS_CONFIRM_PAGE_ENABLED: 'trfree_confirm_page_enabled',
  /** A/B: hides the flexible-dates (±3 days) option from the tours search for the treatment group, and collapses a saved flexible search back to its exact date. Treatment variant key: `flexible_dates_disabled`. */
  TOURS_FLEXIBLE_DATES_DISABLED: 'trfree_flexible_dates_disabled',
  AVIA_TOUR_PROMO: 'trfree_avia_tour_promo',
};

const AVIA_FLAGS = {
  FARE_FAMILIES_FLEXIBLE: 'avfree_fare_families_flexible',
  CALENDAR_FILTER: 'calendar_filter',
  AVIA_FAVORITES_PAGE: 'avfree_favorites_page',
  SPECIAL_OFFER_FRIENDS: 'avfree_special_offer_friends',
  /**
   * Legacy flag of the first concierge-chat entry on the avia main page
   * (variants `a`/`b`). The redesigned discovery chat is gated by
   * `AI_CHAT_FRIDA` instead — keep this key until the old flag is archived in
   * Amplitude.
   *
   * @deprecated use `AI_CHAT_FRIDA`
   */
  AI_CHAT_ENABLED: 'avfree_ai_chat_enabled',
  /**
   * Release: gates the AI discovery chat «Фрида» (`widgets/ai-chat`) on the
   * avia main page — entry card, chat bottom sheet and its onboarding coach
   * mark. Treatment variant key: `on`, control: `control` (`off` is reserved by
   * Amplitude for the flag's own disabled state). No resolved variant
   * (SDK error, flag missing) = chat hidden.
   */
  AI_CHAT_FRIDA: 'avfree_ai_chat_frida',
  POST_BOOKING_CROSS_SELL: 'cabfree_post_booking_cross_sell',
  /**
   * A/B: gates the referral program in the cabinet as a whole — the entry
   * banner at the bottom of `AccountPage` and the `/referral-program` page it
   * opens. Treatment variant key: `on`; control (and any unresolved variant —
   * experiment stopped, SDK error) sees neither, so the control arm is never
   * shown an entry point that leads nowhere.
   *
   * The same gate holds back `GET /promocodes/referral/info`, which mints the
   * user a personal promo code on its first call: nobody outside the treatment
   * arm should be issued one.
   */
  REFERRAL_PROGRAM: 'cabfree_referral_program',
  /** A/B: off (control) = no recommended-offers block on main page, on = block visible. Treatment variant key: `avfree_recommended_offers_on`. */
  RECOMMENDED_OFFERS: 'avfree_recommended_offers',
  /** A/B: control = legacy weight-based sort, variant `b` = sort search results by backend `ranking`. */
  SEARCH_RESULTS_RANKING: 'avfree_search_results_ranking',
  /** A/B: off (control) = no popular-hotels cross-sell block on the avia main page, on = block visible. Treatment variant key: `on`. */
  AVIA_HOTELS_UPSELL: 'avfree_hotels_upsell',
  /** A/B: control (don't show hotel banner on SERP), on = show per existing Firebase Remote Config logic. Fallback: control. Treatment variant key: `on`. */
  AVFREE_HOTEL_BANNER: 'avfree_hotel_banner',
  /**
   * A/B: gates the whole installment (factoring) feature in avia — the
   * «‹payment› × ‹term›» badge on offers and the payment method on
   * `PaymentPage`. Treatment variant key: `factoring_on`; control (and any
   * unresolved variant — experiment stopped, SDK error) means no installments
   * at all, so the control group is never shown a method it cannot use.
   */
  FACTORING: 'avfree_factoring_on',
  /**
   * Gates the visa-help section entry points in avia and cabinet (routes stay
   * reachable by direct URL) **and** picks which flow collects the documents:
   * `visa_on` — the chat wizard driven by the visa bot, `wizard_on` — the same
   * steps filled in on the country page itself, without a chat or the bot
   * backend. `control` (and no resolved variant) hides the section.
   */
  VISA_HELP: 'avfree_visa_help',
  /** Release: substitutes the visa-wizard hotel booking with a fixture on staging (no bookable stage hotel inventory); prod stays on the real orders API. Treatment variant key: `mock_on`. */
  VISA_HOTELS_MOCK: 'avfree_visa_hotels_mock',
};

const HOTELS_FLAGS = {
  /**
   * A/B/C on how price, cashback and taxes are laid out in the hotels offer and
   * rate cards. `control` (and any unresolved variant) keeps the current layout;
   * `a` strips included taxes out of the headline price (cashback recalculated
   * off the net price, taxes shown as a separate note); `b` swaps price/cashback
   * order and shows the cashback amount instead of the net price. Variant keys:
   * `control`, `a`, `b`. Consumed via `features/price-display-experiment` in
   * freedom-hotels.
   */
  HOTELS_PRICE_DISPLAY: 'htfree_price_display',
};

export const experimentsFlags = {
  ...AVIA_FLAGS,
  ...TOURS_FLAGS,
  ...HOTELS_FLAGS,
};

/**
 * Experiments that are shared across apps and should be cached in localStorage.
 * Only these experiments will persist their variant between app navigations.
 * This prevents UI flicker when the Amplitude SDK returns stale cached results
 * from a different app (e.g. railways with a different user ID).
 */
export const CROSS_APP_CACHED_EXPERIMENTS: ReadonlySet<string> = new Set([
  TOURS_FLAGS.AVIA_TOUR_PROMO,
]);
